import { UseFormReturn } from 'react-hook-form';
import { Input } from '@theraply/ui';
import { AcademicCapIcon, IdentificationIcon, CalendarIcon } from '@heroicons/react/24/outline';
import { ProfessionalInfoData } from '../../../hooks/useRegistrationWizard';

interface ProfessionalInfoStepProps {
  form: UseFormReturn<ProfessionalInfoData>;
}

const LICENSE_TYPES = [
  { value: 'LCSW', label: 'LCSW - Licensed Clinical Social Worker' },
  { value: 'LPC', label: 'LPC - Licensed Professional Counselor' },
  { value: 'LMFT', label: 'LMFT - Licensed Marriage and Family Therapist' },
  { value: 'PhD', label: 'PhD - Doctor of Philosophy' },
  { value: 'PsyD', label: 'PsyD - Doctor of Psychology' },
  { value: 'Licensed Psychologist', label: 'Licensed Psychologist' },
];

const US_STATES = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
  { value: 'DC', label: 'District of Columbia' },
];

export function ProfessionalInfoStep({ form }: ProfessionalInfoStepProps) {
  const {
    register,
    formState: { errors },
  } = form;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Professional Information</h2>
        <p className="mt-2 text-sm text-gray-600">
          Provide your professional credentials and licensing information.
        </p>
      </div>

      <div>
        <label htmlFor="licenseType" className="block text-sm font-medium text-gray-700">
          License Type <span className="text-red-500">*</span>
        </label>
        <div className="mt-1.5 relative">
          <AcademicCapIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
          <select
            {...register('licenseType')}
            id="licenseType"
            className="block w-full rounded-lg border border-gray-300 pl-10 pr-3 py-2 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
          >
            <option value="">Select a license type</option>
            {LICENSE_TYPES.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>
        {errors.licenseType && (
          <p className="mt-1.5 text-sm text-red-600">{errors.licenseType.message}</p>
        )}
      </div>

      <div>
        <label htmlFor="state" className="block text-sm font-medium text-gray-700">
          State of Licensure <span className="text-red-500">*</span>
        </label>
        <div className="mt-1.5">
          <select
            {...register('state')}
            id="state"
            className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
          >
            <option value="">Select a state</option>
            {US_STATES.map((state) => (
              <option key={state.value} value={state.value}>
                {state.label}
              </option>
            ))}
          </select>
        </div>
        {errors.state && (
          <p className="mt-1.5 text-sm text-red-600">{errors.state.message}</p>
        )}
      </div>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <div>
          <Input
            {...register('licenseNumber')}
            label="License Number"
            placeholder="e.g., 12345"
            required
            error={errors.licenseNumber?.message}
            startIcon={<IdentificationIcon className="h-5 w-5" />}
            fullWidth
          />
        </div>

        <div>
          <Input
            {...register('licenseExpiration')}
            type="date"
            label="License Expiration Date"
            required
            error={errors.licenseExpiration?.message}
            startIcon={<CalendarIcon className="h-5 w-5" />}
            fullWidth
          />
        </div>
      </div>

      <div>
        <Input
          {...register('npiNumber')}
          label="NPI Number"
          placeholder="1234567890"
          error={errors.npiNumber?.message}
          helperText="Optional: 10-digit National Provider Identifier"
          maxLength={10}
          fullWidth
        />
      </div>

      <div className="rounded-lg bg-amber-50 p-4 border border-amber-200">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-amber-400"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-amber-800">Verification Required</h3>
            <div className="mt-2 text-sm text-amber-700">
              <p>
                All professional credentials will be verified by our compliance team. You'll need
                to upload supporting documentation in the next step. Providing accurate information
                helps expedite the verification process.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
